import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ThreeDotsIcon from 'assets/images/rectangle/three-dots.svg'
interface DropDownProps {
    listWrapperClassName?: string
    children: React.ReactNode
    // Android PopupBalloonMenu shows the member-row menu ABOVE the anchor
    // with a down-pointing arrow; the toolbar menu drops BELOW the toolbar.
    align?: 'top' | 'bottom'
    // arrow color must match the menu surface (only drawn for align="top")
    arrowColor?: string
}
// The menu is rendered in a portal on <body> with position:fixed so it can
// never be painted over by sibling cards (each card is its own positioned
// element, so an in-card absolute menu loses to later siblings in paint
// order regardless of z-index).
const CustomDropDown = ({
    listWrapperClassName = '',
    children,
    align = 'bottom',
    arrowColor = '#FFFFFF',
}: DropDownProps) => {
    const triggerRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [pos, setPos] = useState<{
        top: number
        left: number
        arrowLeft: number
    } | null>(null)

    useLayoutEffect(() => {
        if (!isOpen) {
            setPos(null)
            return
        }
        const t = triggerRef.current?.getBoundingClientRect()
        const m = menuRef.current?.getBoundingClientRect()
        if (!t || !m) return
        let left = Math.min(t.left, window.innerWidth - m.width - 8)
        left = Math.max(8, left)
        let top: number
        if (align === 'top') {
            top = t.top - m.height - 10
            if (top < 8) top = t.bottom + 10
        } else {
            top = t.bottom + 4
            if (top + m.height > window.innerHeight - 8) {
                top = Math.max(8, t.top - m.height - 4)
            }
        }
        const arrowLeft = Math.min(
            Math.max(t.left + t.width / 2 - left - 8, 12),
            m.width - 28
        )
        setPos({ top, left, arrowLeft })
    }, [isOpen, align])

    useEffect(() => {
        if (!isOpen) return
        const close = () => setIsOpen(false)
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as Node
            if (triggerRef.current?.contains(target)) return
            if (menuRef.current?.contains(target)) return
            close()
        }
        document.addEventListener('click', onDocClick)
        window.addEventListener('scroll', close, true)
        window.addEventListener('resize', close)
        return () => {
            document.removeEventListener('click', onDocClick)
            window.removeEventListener('scroll', close, true)
            window.removeEventListener('resize', close)
        }
    }, [isOpen])

    return (
        <div className="relative" ref={triggerRef}>
            <img
                src={ThreeDotsIcon}
                alt="three-dots"
                onClick={() => setIsOpen(!isOpen)}
            />
            {isOpen &&
                createPortal(
                    <div
                        ref={menuRef}
                        className={`fixed z-[1050] ${listWrapperClassName}`}
                        style={{
                            top: pos ? pos.top : -9999,
                            left: pos ? pos.left : -9999,
                            visibility: pos ? 'visible' : 'hidden',
                        }}
                        onClick={() => setIsOpen(false)}
                    >
                        {children}
                        {align === 'top' && (
                            <span
                                className="absolute top-full h-0 w-0 border-x-8 border-t-8 border-x-transparent"
                                style={{
                                    left: pos ? pos.arrowLeft : 0,
                                    borderTopColor: arrowColor,
                                }}
                            />
                        )}
                    </div>,
                    document.body
                )}
        </div>
    )
}

export default CustomDropDown
